import { createContext, useContext, useEffect, useState } from "react";
import {
  convertStripeDataToBillingConfig,
  getProductsAndPrices,
  type BillingConfig,
  type ProductsResponse,
} from "../services/BillingProductsService";
import useLoader from "../hooks/useLoader";

const BillingContext = createContext<{
  billingConfig: BillingConfig | null;
  productsData: ProductsResponse | null;
  loadBillingConfig: () => Promise<void>;
  isLoading: boolean;
  getStoreLimit: (retailer: string, retailers: string[]) => number;
}>({
  billingConfig: null,
  productsData: null,
  loadBillingConfig: () => Promise.resolve(),
  isLoading: false,
  getStoreLimit: () => 0,
});

export function useBillingContext() {
  return useContext(BillingContext);
}

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(
    null
  );
  const [productsData, setProductsData] = useState<ProductsResponse | null>(
    null
  );

  const { isLoading, stop } = useLoader(true);

  async function loadBillingConfig() {
    try {
      const products = await getProductsAndPrices();
      setProductsData(products);
      const config = convertStripeDataToBillingConfig(products);
      setBillingConfig(config);
      stop();
    } catch (error) {
      console.error("Failed to load billing config:", error);
      stop();
      throw error;
    }
  }

  function getStoreLimit(re: string, retailers: string[]): number {
    for (const retailer of retailers) {
      const retailerConfig = billingConfig?.[retailer];
      if (retailerConfig) {
        const found = retailerConfig.find((r) => r.retailer?.startsWith(re));
        if (!found) continue;
        return found.storeLimit;
      }
    }
    return 0;
  }

  useEffect(() => {
    // loadBillingConfig re-throws for callers that await it; here nothing can
    // consume the rejection, so swallow it (it's already logged) to keep a
    // failed fetch from surfacing as an unhandled promise rejection.
    loadBillingConfig().catch(() => {});
  }, []);

  return (
    <BillingContext.Provider
      value={{ billingConfig, productsData, loadBillingConfig, isLoading, getStoreLimit }}
    >
      {children}
    </BillingContext.Provider>
  );
}
